/**
 * Permission refusals in plain words.
 *
 * A Firestore rule that says no surfaces as a FirebaseError with code
 * 'permission-denied' and the message "Missing or insufficient permissions."
 * That sentence tells a person nothing they can act on and reads like the
 * app is broken in a way they caused. It sat above TO DO on Kim's home
 * screen on 2026-09-10. lib/injuryNotes.ts and lib/dancerTodos.ts already
 * translate it for their listeners; this is the same idea for a write.
 *
 * Firebase-free on purpose (a plain node test imports it). It looks only at
 * the shape a FirebaseError has, and at the wrapped cause a write helper may
 * have put it behind.
 */

const RAW_PERMISSION_MESSAGE = /missing or insufficient permissions/i;

function codeOf(err: unknown): string {
  if (!err || typeof err !== 'object') return '';
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code : '';
}

/** True for a Firestore rule refusal, however many wrappers deep. */
export function isPermissionDenied(err: unknown, depth = 0): boolean {
  if (!err || depth > 4) return false;
  const code = codeOf(err);
  if (code === 'permission-denied' || code === 'firestore/permission-denied') return true;
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  if (RAW_PERMISSION_MESSAGE.test(message)) return true;
  if (typeof err === 'object') {
    const cause = (err as { cause?: unknown }).cause;
    if (cause && cause !== err) return isPermissionDenied(cause, depth + 1);
  }
  return false;
}

/** A refusal on the Attendance page: the likely cause is a one-sided enrollment. */
export const ATTENDANCE_MARK_REFUSED =
  'The Hub refused that mark. This dancer may not be enrolled in this class in the Hub; check the class roster.';

/** A refusal on an ordinary to-do, announcement or post action. */
export const ACTION_REFUSED = 'The Hub refused that. Your sign-in is not allowed to change this one.';

/**
 * The message a screen shows for a failed action: the plain refusal when
 * the rules said no, otherwise the error's own words. The raw Firestore
 * string never reaches the screen either way.
 */
export function actionErrorMessage(err: unknown, refusal: string = ACTION_REFUSED): string {
  if (isPermissionDenied(err)) return refusal;
  return err instanceof Error ? err.message : String(err);
}
